/**
 * How often, and how, somebody on the payroll is paid (the owner, 2026-10-12: «we need more options
 * on how to hire somebody and their pay schedule. Some people want to be paid weekly, others are
 * going to be paid BY the amount of days they work so not like a regular employee»).
 *
 * Each person is paid every week, every fifteen days or every month: a fixed amount each time, or so
 * much for each day worked. A run of the payroll pays the people of one schedule for one stretch of
 * days, and what each stretch is lives here, shared by the books, which refuse a stretch that is not
 * one, and the screen, which offers only those.
 */

import { diasEntre, sumarDias } from './fechas.js';

export const FRECUENCIAS_PAGO = ['semanal', 'quincenal', 'mensual'] as const;
export type FrecuenciaPago = (typeof FRECUENCIAS_PAGO)[number];

/** How many times a year each schedule pays: what the income tax projects a year from. */
export const PAGOS_POR_ANIO: Record<FrecuenciaPago, number> = { semanal: 52, quincenal: 24, mensual: 12 };

/** A stretch of days, both ends included, as `YYYY-MM-DD`. */
export interface Rango {
  desde: string;
  hasta: string;
}

/** The last day of a `YYYY-MM` month. */
export function finDeMes(periodo: string): string {
  const [anio, mes] = periodo.split('-').map(Number);
  const ultimo = new Date(Date.UTC(anio!, mes!, 0)).getUTCDate();
  return `${periodo}-${String(ultimo).padStart(2, '0')}`;
}

export const rangoDelMes = (periodo: string): Rango => ({ desde: `${periodo}-01`, hasta: finDeMes(periodo) });

/** The first fifteen days of a month, or the sixteenth to its end. */
export const rangoDeQuincena = (periodo: string, segunda: boolean): Rango =>
  segunda ? { desde: `${periodo}-16`, hasta: finDeMes(periodo) } : { desde: `${periodo}-01`, hasta: `${periodo}-15` };

/** The seven days that end on `hasta`, the payday. */
export const rangoDeSemana = (hasta: string): Rango => ({ desde: sumarDias(hasta, -6), hasta });

const mismo = (a: Rango, b: Rango) => a.desde === b.desde && a.hasta === b.hasta;

/** Whether a stretch of days is one this schedule pays: a week, half a month, a month. */
export function esRangoDe(frecuencia: FrecuenciaPago, r: Rango): boolean {
  const periodo = r.hasta.slice(0, 7);
  switch (frecuencia) {
    case 'semanal':
      return diasEntre(r.desde, r.hasta) === 6;
    case 'quincenal':
      return mismo(r, rangoDeQuincena(periodo, false)) || mismo(r, rangoDeQuincena(periodo, true));
    case 'mensual':
      return mismo(r, rangoDelMes(periodo));
  }
}

/** Whether two stretches share a day. */
export const seCruzan = (a: Rango, b: Rango): boolean => a.desde <= b.hasta && b.desde <= a.hasta;

/** The months a stretch touches, as `YYYY-MM`, in order. */
export function mesesDe(r: Rango): string[] {
  const meses: string[] = [];
  let [anio, mes] = r.desde.slice(0, 7).split('-').map(Number) as [number, number];
  const ultimo = r.hasta.slice(0, 7);
  for (;;) {
    const periodo = `${anio}-${String(mes).padStart(2, '0')}`;
    meses.push(periodo);
    if (periodo >= ultimo) return meses;
    mes += 1;
    if (mes > 12) {
      mes = 1;
      anio += 1;
    }
  }
}

/** The month after a `YYYY-MM` one. */
export function mesSiguiente(periodo: string): string {
  const [anio, mes] = periodo.split('-').map(Number) as [number, number];
  return mes === 12 ? `${anio + 1}-01` : `${anio}-${String(mes + 1).padStart(2, '0')}`;
}

/** The month before a `YYYY-MM` one. */
export function mesAnterior(periodo: string): string {
  const [anio, mes] = periodo.split('-').map(Number) as [number, number];
  return mes === 1 ? `${anio - 1}-12` : `${anio}-${String(mes - 1).padStart(2, '0')}`;
}

/**
 * What a pay comes to in a month, in centavos: the books' measure of a salary for severance, and
 * what a phone that has not updated reads as the monthly salary. A day's pay counts thirty days,
 * the way the law turns a monthly salary into a daily one; a week's, fifty-two weeks over twelve.
 */
export function equivalenteMensual(frecuencia: FrecuenciaPago, porDia: boolean, monto: number): number {
  if (porDia) return monto * 30;
  return Math.round((monto * PAGOS_POR_ANIO[frecuencia]) / 12);
}
